import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User } from 'lucide-react';

const Navbar = ({ hideLogin }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Programme', path: '/categories' },
    { name: 'Corporate', path: '/corporate-training' },
    { name: 'Counselling', path: '/counselling' },
    { name: 'Events', path: '/events' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav className="sticky top-0 z-50 bg-exiser-primary text-white border-b-4 border-exiser-accent shadow-xl font-oswald tracking-wide">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">

          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="flex items-center">
              <img
                src="/logo.png"
                alt="Exiser Logo"
                className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 object-contain transition-transform duration-300 hover:scale-105"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-4 xl:space-x-6">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`text-sm font-bold uppercase transition-all duration-200 hover:text-exiser-accent ${isActive(link.path)
                    ? 'text-exiser-accent scale-110'
                    : 'text-gray-200'
                  }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Desktop CTA + Customer Login */}
          <div className="hidden lg:flex items-center space-x-4">

            {/* ✨ Animated Login Button */}
            <Link
              to="/customer-login"
              className="group flex items-center gap-2 relative overflow-hidden
                        border border-white/40 text-white px-5 py-2 text-sm uppercase 
                        font-bold rounded-md transition-all duration-300
                        hover:bg-white hover:text-exiser-primary hover:border-white 
                        shadow-lg hover:shadow-[0_0_18px_rgba(255,255,255,0.5)]
                        hover:scale-105"
            >
              <User className="w-4 h-4 transition-transform duration-300 group-hover:rotate-12 group-hover:scale-110" />
              <span className="tracking-widest">Login</span>
              <span className="absolute inset-0 -z-10 bg-white opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500"></span>
            </Link>

            {/* Book Session Button */}
            <Link
              to="/book"
              className="bg-exiser-accent hover:bg-yellow-300 text-exiser-primary font-bold py-3 px-6 text-sm uppercase tracking-wider transition-all transform hover:-translate-y-1 shadow-[0_0_15px_rgba(250,204,21,0.5)] rounded-sm animate-[pulse_2s_ease-in-out_infinite] hover:animate-none"
            >
              Book Session
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="flex lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              type="button"
              className="text-white hover:text-exiser-accent p-2"
            >
              {isMenuOpen ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-exiser-primary border-t border-purple-800 absolute w-full shadow-xl z-50">
          <div className="px-4 pt-2 pb-6 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className="block px-3 py-4 text-lg font-bold uppercase text-white hover:bg-purple-800 hover:text-exiser-accent border-b border-purple-800"
              >
                {link.name}
              </Link>
            ))}

            {/* ✨ Mobile Login Button */}
            <Link
              to="/customer-login"
              onClick={() => setIsMenuOpen(false)}
              className="w-full flex items-center justify-center gap-2 mt-4 
                         border border-white/30 text-white py-3 uppercase font-bold 
                         rounded-md transition-all duration-300
                         hover:bg-white hover:text-exiser-primary hover:shadow-lg hover:scale-105"
            >
              <User className="w-5 h-5" /> Login
            </Link>

            {/* Book Session Button */}
            <Link
              to="/book"
              onClick={() => setIsMenuOpen(false)}
              className="block w-full text-center mt-4 bg-exiser-accent text-exiser-primary font-bold py-4 px-6 uppercase animate-[pulse_2s_ease-in-out_infinite]"
            >
              Book Session
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;